import { Injectable } from '@angular/core';
import { environment } from '../../environments/environment';
import { HttpService } from '../_shared/services/http.service';
import { ConfigitQuote, QuotationQuote, QuoteService } from '../_shared/services/quote.service';
import {
    OfferCreationResponse,
    OfferParams,
    Prices,
    PriceSummary,
    SetQuoteAsClosedParams,
} from '../_shared/components/price-summary/price-summary.model';
import { ProductGroup } from '../_shared/services/products.service';
import { PriceSummaryHelperService } from '../_shared/components/price-summary/price-summary.helper.service';
import { concatMap, map } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';
import { Observable } from 'rxjs';

@Injectable({
    providedIn: 'root',
})
export class SummaryDataService {
    constructor(
        private httpService: HttpService,
        private helperService: PriceSummaryHelperService,
        private translate: TranslateService,
        private quoteService: QuoteService
    ) {}

    public closeQuote(
        customerId: string,
        voucherCode: string,
        quotationRequestId: string,
        groupedProducts: ProductGroup[],
        lastPriceSummary: PriceSummary
    ): Observable<{ salesDocumentNumber: string }> {
        const productList = groupedProducts.flatMap((group) =>
            group.products.map((product) => ({ ...product, group: this.translate.instant(group.translationKey) }))
        );
        const quote = this.quoteService.getCurrentQuote();
        const payload = this.helperService.getOfferParams(customerId, voucherCode, productList, quote);
        payload.salesforceRequestId = quotationRequestId;

        return quote.isLegacy
            ? this.closeConfigitQuote(payload, quote.configitQuote, <Prices>lastPriceSummary.prices)
            : this.closeQuotationQuote(payload, quote.quotationQuote);
    }

    public getAvailablePrintTypes(salesOrg: string) {
        const url = `${environment.http.quotation}offers/print/types?salesOrg=${salesOrg}`;
        return this.httpService.get(url);
    }

    public addReportingEntry(payload: object) {
        const url = `${environment.http.configuration}reporting`;
        return this.httpService.post(url, {
            appName: 'Quote Assist Pro',
            ...payload,
        });
    }

    private closeQuotationQuote(payload: OfferParams, quote: QuotationQuote) {
        const url = `${environment.http.quotation}quotations/quote-assist-pro/quotes/${quote.id}/close`;
        return this.httpService
            .post<QuotationQuote>(url, payload)
            .pipe(map((response) => ({ salesDocumentNumber: response.offerNumber })));
    }

    private closeConfigitQuote(payload: OfferParams, quote: ConfigitQuote, prices: Prices) {
        const url = `${environment.http.quotation}offers`;
        return this.httpService.post<OfferCreationResponse>(url, payload).pipe(
            concatMap(({ documentNumber, expirationDate }) =>
                this.setQuoteAsClosed(quote, {
                    sapDocumentNumber: documentNumber,
                    expirationDate,
                    customerId: payload.customerNumber,
                    prices,
                    voucherCode: payload.voucherId,
                })
            )
        );
    }

    private setQuoteAsClosed({ documentId, revision }: ConfigitQuote, payload: SetQuoteAsClosedParams) {
        const url = `${environment.http.baseUrl}quote/${documentId}/revision/${revision}/close`;
        return this.httpService.patch<{ salesDocumentNumber: string }>(url, payload);
    }
}
